import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import {
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  DocumentMagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  ArrowUpCircleIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from "recharts";
import { ROUTES } from "@/shared/core";
import type { FeatureType } from "@/shared/core";
import type { PlanLimits } from "@/shared/db";

interface UsageItem {
  featureType: FeatureType;
  usage: number;
  limit: number;
  labelKey: string;
  icon: React.ElementType;
  color: string;
}

interface UsageAndLimitsSectionProps {
  limits: PlanLimits | null;
  getUsage: (ft: FeatureType) => Promise<number>;
  formatPlanName: (planType: string | null) => string;
  planType: string | null;
}

const FEATURE_CONFIG: Array<{
  ft: FeatureType;
  limitKey: keyof PlanLimits;
  labelKey: string;
  icon: React.ElementType;
  color: string;
}> = [
  {
    ft: "document_generation",
    limitKey: "documents_per_month",
    labelKey: "lawyer.dashboard.usage.documents",
    icon: DocumentTextIcon,
    color: "#0d9488",
  },
  {
    ft: "consultation",
    limitKey: "consultations_per_month",
    labelKey: "lawyer.dashboard.usage.consultations",
    icon: QuestionMarkCircleIcon,
    color: "#f59e0b",
  },
  {
    ft: "document_review",
    limitKey: "document_reviews_per_month",
    labelKey: "lawyer.dashboard.usage.documentReviews",
    icon: DocumentMagnifyingGlassIcon,
    color: "#8b5cf6",
  },
  {
    ft: "legal_ai_chat",
    limitKey: "legal_ai_queries_per_month",
    labelKey: "lawyer.dashboard.usage.legalAiChat",
    icon: ChatBubbleLeftRightIcon,
    color: "#06b6d4",
  },
];

export const UsageAndLimitsSection: React.FC<UsageAndLimitsSectionProps> = ({
  limits,
  getUsage,
  formatPlanName,
  planType,
}) => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const [usageItems, setUsageItems] = useState<UsageItem[]>([]);
  const [loadingUsage, setLoadingUsage] = useState(true);

  useEffect(() => {
    if (!limits) return;

    const loadUsage = async () => {
      setLoadingUsage(true);
      const items: UsageItem[] = [];
      for (const f of FEATURE_CONFIG) {
        const limitVal = limits[f.limitKey];
        if (limitVal === null || limitVal === undefined) continue;
        const limit = Number(limitVal);
        if (limit <= 0) continue;
        const usage = await getUsage(f.ft);
        items.push({
          featureType: f.ft,
          usage,
          limit,
          labelKey: f.labelKey,
          icon: f.icon,
          color: f.color,
        });
      }
      setUsageItems(items);
      setLoadingUsage(false);
    };

    loadUsage();
  }, [limits, getUsage]);

  const chartData = usageItems.map((i) => ({
    name: t(i.labelKey),
    used: i.usage,
    limit: i.limit,
    pct: Math.min(100, Math.round((i.usage / i.limit) * 100)),
    fill: i.color,
  }));

  if (usageItems.length === 0 && !loadingUsage) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.0, duration: 0.4 }}
      className="mb-8"
    >
      <div className="bg-white rounded-xl shadow-lg border border-tiber-100 overflow-hidden">
        <div className="bg-linear-to-r from-tiber-50 via-atlantis-50/30 to-tiber-50 px-6 py-5 border-b border-tiber-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-tiber-900 flex items-center gap-2">
                <SparklesIcon className="w-6 h-6 text-tiber-500" />
                {t("lawyer.dashboard.usage.title", "Uso y Límites")}
              </h2>
              <p className="mt-1 text-sm text-tiber-600">
                {t(
                  "lawyer.dashboard.usage.subtitle",
                  "Rastrea tu uso este mes",
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-tiber-100 text-tiber-800">
                {formatPlanName(planType)}
              </span>
              <Link
                to={ROUTES.PRICING}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 bg-tiber-600 text-white hover:bg-tiber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-600"
              >
                <ArrowUpCircleIcon className="w-5 h-5" />
                {t("lawyer.dashboard.usage.upgrade", "Mejorar Plan")}
              </Link>
            </div>
          </div>
        </div>

        <div className="p-6">
          {loadingUsage ? (
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
              aria-busy="true"
            >
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-28 bg-tiber-50 animate-pulse rounded-xl"
                />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {usageItems.map((item, idx) => (
                  <motion.div
                    key={item.featureType}
                    initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: idx * 0.05,
                      duration: prefersReducedMotion ? 0 : 0.3,
                    }}
                    className="relative bg-linear-to-br from-white to-tiber-50/30 rounded-xl p-5 border border-tiber-100 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="p-2.5 rounded-xl shadow-sm"
                        style={{ backgroundColor: `${item.color}18` }}
                      >
                        <item.icon
                          className="w-6 h-6"
                          style={{ color: item.color }}
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-tiber-900 text-sm">
                          {t(item.labelKey)}
                        </p>
                        <p className="text-xs text-tiber-500 tabular-nums">
                          {item.usage} / {item.limit}{" "}
                          {t("lawyer.dashboard.usage.thisMonth", "este mes")}
                        </p>
                      </div>
                    </div>
                    <div className="h-2.5 bg-tiber-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{
                          width: `${Math.min(100, (item.usage / item.limit) * 100)}%`,
                        }}
                        transition={{
                          duration: prefersReducedMotion ? 0 : 0.6,
                          delay: idx * 0.05,
                        }}
                        className="h-full rounded-full"
                        style={{
                          background: `linear-gradient(90deg, ${item.color}, ${item.color}cc)`,
                          boxShadow: `0 0 12px ${item.color}40`,
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>

              {chartData.length > 0 && (
                <div className="bg-linear-to-br from-tiber-50/50 to-atlantis-50/30 rounded-xl p-6 border border-tiber-100">
                  <h3 className="text-lg font-bold text-tiber-900 mb-6">
                    {t("lawyer.dashboard.usage.chartTitle", "Resumen de Uso")}
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 8, right: 24, left: 24, bottom: 8 }}
                      >
                        <XAxis
                          type="number"
                          domain={[0, 100]}
                          tick={{ fontSize: 12, fill: "#64748b" }}
                          tickFormatter={(v) => `${v}%`}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={140}
                          tick={{
                            fontSize: 13,
                            fill: "#0A1C2E",
                            fontWeight: 500,
                          }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: 12,
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 10px 40px -10px rgba(0,0,0,0.2)",
                          }}
                          formatter={(value, _name, item) => {
                            const p = item?.payload as { used?: number; limit?: number } | undefined;
                            const v = typeof value === "number" ? value : 0;
                            return [
                              `${p?.used ?? 0} / ${p?.limit ?? 0}`,
                              p?.limit ? `${v}%` : "",
                            ];
                          }}
                        />
                        <Bar
                          dataKey="pct"
                          radius={[0, 8, 8, 0]}
                          maxBarSize={32}
                          fill="#1cb0a9"
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={index}
                              fill={entry.pct >= 90 ? "#dc2626" : entry.fill}
                              fillOpacity={entry.pct >= 90 ? 1 : 0.85}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </motion.section>
  );
};
